import React, { memo } from 'react';
import { Button, Flex, Text, Collapse, FlexColumn, CalloutOutline, Tooltip } from 'core/components';
import CopyToClipboard from 'app/components/CopyToClipboardButton';
import { FiCopy } from 'react-icons/fi';
import Markdown from '../../journeys/Markdown';
import { ReasoningDetails, ReasoningContent, StepContainer } from '../KaizenChatSession.styles';
import KaizenToolResult from '../KaizenToolResult';
import ThinkingIndicator from './ThinkingIndicator';

// Memoized step component to prevent unnecessary re-renders
const StepComponent = memo(
  ({
    step,
    stepIndex,
    isStepReasoningOpen,
    onToggleReasoning,
    onSelectToolResult,
    isCopilotMode,
    isExistingChatLoading
  }) => {
    const stepId = step.id || stepIndex;
    const { toolCalls } = step;
    const isFinalAnswer = step.llm_response?.final_answer && step.processing_status === 'COMPLETED';

    return (
      <StepContainer>
        {step.llm_response?.reasoning && (
          <Flex alignItems="center" gap={1}>
            <Button
              small
              minimal
              color="muted"
              icon={isStepReasoningOpen ? 'caret-up' : 'caret-down'}
              style={{ alignSelf: 'flex-start' }}
              text={isStepReasoningOpen ? 'Hide reasoning' : 'Show reasoning'}
              onClick={() => onToggleReasoning(stepId)}
            />
          </Flex>
        )}

        <Flex flexDirection="column" gap={1}>
          {step.llm_response?.reasoning && (
            <Collapse isOpen={isStepReasoningOpen} title="Reasoning" defaultOpen>
              <ReasoningDetails>
                <ReasoningContent>
                  <Markdown>{step.llm_response.reasoning}</Markdown>
                </ReasoningContent>
              </ReasoningDetails>
            </Collapse>
          )}

          {isFinalAnswer && (
            <Flex flexDirection="column" gap={1}>
              <Markdown>{step.llm_response.final_answer}</Markdown>
              <Tooltip content="Copy response" position="bottom">
                <CopyToClipboard intent="default" text={step.llm_response.final_answer}>
                  <Button minimal icon={FiCopy} />
                </CopyToClipboard>
              </Tooltip>
            </Flex>
          )}

          {toolCalls && (
            <FlexColumn flexDirection="column" gap="8px">
              {toolCalls?.map((toolCall) => (
                <KaizenToolResult
                  key={toolCall.id || toolCall.tool_tracking_id}
                  tool={toolCall}
                  step={step}
                  onSelectToolResult={onSelectToolResult}
                  isCopilotMode={isCopilotMode}
                  isExistingChatLoading={isExistingChatLoading}
                />
              ))}
            </FlexColumn>
          )}

          {step.error_message && (
            <CalloutOutline intent="danger">
              <Text small>Error: something went wrong</Text>
            </CalloutOutline>
          )}

          {step.processing_status === 'PROCESSING' &&
            (!step.toolCalls || step.toolCalls.every((tc) => !tc.status)) &&
            !step.llm_response?.final_answer && (
              <Flex alignItems="center" gap={1}>
                <ThinkingIndicator />
              </Flex>
            )}
        </Flex>
      </StepContainer>
    );
  }
);

StepComponent.displayName = 'StepComponent';

export default StepComponent;
